import {
    effect,
    Inject,
    Injectable,
    isDevMode,
    OnDestroy,
    PLATFORM_ID,
    signal,
} from '@angular/core';
import { SwUpdate, VersionEvent } from '@angular/service-worker';
import { isPlatformBrowser } from '@angular/common';
import { interval, Subscription } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LoggingService } from './logging.service';
import { PendingRequestsService } from './pending-requests.service';
import { AppConfigService } from './app-config.service';

/**
 * Service responsible for managing application updates using Angular's Service Worker.
 * This service handles version updates, periodic update checks, and ensures the application
 * reloads when a new version is available.
 *
 * Key Features:
 * - Subscribes to version update events emitted by the Angular Service Worker.
 * - Logs version-related events using the `LoggingService`.
 * - Ensures pending requests are completed before reloading the application.
 * - Periodically checks for updates and handles retries in case of errors.
 *
 * Dependencies:
 * - `SwUpdate`: Angular Service Worker update service.
 * - `LoggingService`: Custom service for logging application events.
 * - `PendingRequestsService`: Service to track pending HTTP requests.
 * - `AppConfigService`: Provides application configuration, including update intervals.
 * - `PLATFORM_ID`: Angular token to determine the current platform (browser/server).
 *
 * This service is decorated with `@Injectable` to allow dependency injection
 * and `@UntilDestroy` to automatically clean up subscriptions.
 */
@Injectable({ providedIn: 'root' })
@UntilDestroy()
export class AppUpdateService implements OnDestroy {
    private updateCheckSubscription?: Subscription;
    private readonly shouldUpdate = signal(false);
    private retryCount = 0;

    constructor(
        private readonly swUpdate: SwUpdate,
        private readonly loggingService: LoggingService,
        private readonly pendingRequestService: PendingRequestsService,
        private readonly appConfig: AppConfigService,
        @Inject(PLATFORM_ID) private readonly platformId: Object,
    ) {
        this.initializeService();
    }

    ngOnDestroy(): void {
        this.updateCheckSubscription?.unsubscribe();
    }

    private initializeService(): void {
        console.log('AppUpdateService initialized');
        if (!this.shouldInitialize()) {
            console.log('AppUpdateService not initialized');
            return;
        }

        console.log('AppUpdateService is running in production mode on the browser');

        this.setupUpdateEffect();
        this.subscribeToVersionUpdates();
        this.startPeriodicUpdateCheck();
    }

    private shouldInitialize(): boolean {
        return isPlatformBrowser(this.platformId) && !isDevMode();
    }

    private setupUpdateEffect(): void {
        effect(() => {
            if (this.shouldUpdate() && !this.pendingRequestService.hasPending()) {
                console.info('No pending requests, proceeding with app update2');
                this.shouldUpdate.set(false);
                this.reloadApplication();
            }
        });
    }

    private subscribeToVersionUpdates(): void {
        this.swUpdate.versionUpdates.pipe(untilDestroyed(this)).subscribe({
            next: (event) => this.handleVersionEvent(event),
            error: (error) => this.handleVersionUpdateError(error),
        });
    }

    private handleVersionEvent(event: VersionEvent): void {
        switch (event.type) {
            case 'VERSION_DETECTED':
                this.handleVersionDetected(event);
                break;
            case 'VERSION_READY':
                this.handleVersionReady(event);
                break;
            case 'VERSION_INSTALLATION_FAILED':
                this.handleVersionInstallationFailed(event);
                break;
        }
    }

    private handleVersionDetected(
        event: Extract<VersionEvent, { type: 'VERSION_DETECTED' }>,
    ): void {
        console.info('New version detected: ', event.version);
        this.loggingService.logFeature({
            featureName: 'App Update',
            tags: ['app-update', 'version-detected'],
            additionalInfo: {
                name: 'VersionDetected',
                value: {
                    version: event.version.hash,
                    url: location?.href,
                },
            },
        });
    }

    private handleVersionReady(event: Extract<VersionEvent, { type: 'VERSION_READY' }>): void {
        console.info(
            `New Version ready for use: Current version: ${event.currentVersion.hash}, Latest version: ${event.latestVersion.hash}`,
        );
        this.loggingService.logFeature({
            featureName: 'App Update',
            tags: ['app-update', 'version-ready'],
            additionalInfo: {
                name: 'VersionReady',
                value: {
                    currentVersion: event.currentVersion.hash,
                    latestVersion: event.latestVersion.hash,
                },
            },
        });

        this.handleAppUpdate();
    }

    private handleVersionInstallationFailed(
        event: Extract<VersionEvent, { type: 'VERSION_INSTALLATION_FAILED' }>,
    ): void {
        this.loggingService.logFeature({
            featureName: 'App Update Failed',
            tags: ['app-update', 'installation-failed'],
            additionalInfo: {
                name: 'InstallationFailed',
                value: {
                    version: event.version.hash,
                    error: event.error,
                },
            },
        });
    }

    private handleVersionUpdateError(error: unknown): void {
        this.loggingService.logFeature({
            featureName: 'App Update Error',
            tags: ['app-update', 'error'],
            additionalInfo: {
                name: 'VersionUpdateError',
                value: { error: error },
            },
        });
    }

    private startPeriodicUpdateCheck(): void {
        const checkInterval$ = interval(this.appConfig.config().checkIntervalMinutes * 60 * 1000);

        this.updateCheckSubscription = checkInterval$
            .pipe(untilDestroyed(this))
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            .subscribe(() => this.checkForUpdate());
    }

    private async checkForUpdate(): Promise<void> {
        try {
            const hasUpdate = await this.swUpdate.checkForUpdate();

            if (hasUpdate) {
                this.loggingService.logFeature({
                    featureName: 'Detected App Update - Already open in tab',
                    tags: ['app-update', 'periodic-check'],
                });
                this.retryCount = 0; // Reset retry count on successful detection
            }
        } catch (error) {
            this.handleUpdateCheckError(error);
        }
    }

    private handleUpdateCheckError(error: unknown): void {
        this.retryCount++;

        this.loggingService.logFeature({
            featureName: 'App Update',
            tags: ['app-update', 'check-error'],
            additionalInfo: {
                name: 'UpdateCheckError',
                value: {
                    error: error,
                    retryCount: this.retryCount,
                    maxRetries: this.appConfig.config().retryLimit,
                },
            },
        });

        if (this.retryCount >= this.appConfig.config().retryLimit) {
            // Stop periodic checks after max retries
            this.updateCheckSubscription?.unsubscribe();
        }
    }

    private handleAppUpdate() {
        // Wait for pending requests to complete before reloading
        if (this.pendingRequestService.hasPending()) {
            console.info('Waiting for pending requests to complete before updating...');
            this.shouldUpdate.set(true);
        } else {
            this.reloadApplication();
        }
    }

    private reloadApplication(): void {
        console.info('Reloading application...');
        document.location.reload();
    }
}
